import {CA} from '../../../shared/scripts/analytics/Analytics'
import {StorageWrapper} from '../../../shared/scripts/StorageWrapper'
import {Sound} from '../../../shared/scripts/Sound'
import {PIXI} from '@colonist/pixi'
import {UIUtilsEjsVariables} from '../../../shared/scripts/UIUtilsEjsVariables'
import {createDivIn, createSpanIn, setElementInnerHTML} from '../../../shared/scripts/ui_utils/DomUtils'
import {ViewPopupLanguage} from '../../../shared/scripts/ui_popup/ViewPopupLanguage'
import {clientIntl} from '../../../shared/scripts/ClientI18n'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../shared/entity_datas/EntityDatas'
import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {createTooltipIn} from '../../../shared/scripts/ui_utils/TooltipUtils.ts'

enum SocialMediaLinkType {
    Discord = 1,
    Facebook = 2,
    Twitter = 3,
    Instagram = 4,
    Reddit = 5,
    Youtube = 6,
}

export class UIWFooterController {
    private readonly desktopFooter: HTMLDivElement
    protected readonly patchnotesButtons: HTMLCollectionOf<HTMLLinkElement>
    protected readonly rulesButtons: HTMLCollectionOf<HTMLLinkElement>
    protected readonly aboutButtons: HTMLCollectionOf<HTMLLinkElement>
    protected readonly careersButtons: HTMLCollectionOf<HTMLLinkElement>
    protected readonly communityButtons: HTMLCollectionOf<HTMLLinkElement>
    protected readonly faqButtons: HTMLCollectionOf<HTMLLinkElement>
    protected readonly privacyPolicyButtons: HTMLCollectionOf<HTMLLinkElement>
    private readonly discordLinks: HTMLCollectionOf<HTMLLinkElement>
    private readonly facebookLinks: HTMLCollectionOf<HTMLLinkElement>
    private readonly twitterLinks: HTMLCollectionOf<HTMLLinkElement>
    private readonly instagramLinks: HTMLCollectionOf<HTMLLinkElement>
    private readonly redditLinks: HTMLCollectionOf<HTMLLinkElement>
    private readonly youtubeLinks: HTMLCollectionOf<HTMLLinkElement>

    private readonly careersButtonInnerHtml: string
    private readonly inlineBlockText = 'inline-block'
    private readonly autoText = 'auto'

    constructor() {
        this.desktopFooter = document.getElementById('footer-desktop') as HTMLDivElement
        this.patchnotesButtons = document.getElementsByClassName('footer_version') as HTMLCollectionOf<HTMLLinkElement>
        this.rulesButtons = document.getElementsByClassName('footer-rules-button') as HTMLCollectionOf<HTMLLinkElement>
        this.aboutButtons = document.getElementsByClassName('footer-about-button') as HTMLCollectionOf<HTMLLinkElement>
        this.careersButtons = document.getElementsByClassName('footer-careers-button') as HTMLCollectionOf<HTMLLinkElement>
        this.communityButtons = document.getElementsByClassName('footer-community-button') as HTMLCollectionOf<HTMLLinkElement>
        this.faqButtons = document.getElementsByClassName('footer-faq-button') as HTMLCollectionOf<HTMLLinkElement>
        this.privacyPolicyButtons = document.getElementsByClassName('footer-privacy-policy-button') as HTMLCollectionOf<HTMLLinkElement>
        this.discordLinks = document.getElementsByClassName('btn-discord') as HTMLCollectionOf<HTMLLinkElement>
        this.facebookLinks = document.getElementsByClassName('btn-facebook') as HTMLCollectionOf<HTMLLinkElement>
        this.twitterLinks = document.getElementsByClassName('btn-twitter') as HTMLCollectionOf<HTMLLinkElement>
        this.instagramLinks = document.getElementsByClassName('btn-instagram') as HTMLCollectionOf<HTMLLinkElement>
        this.redditLinks = document.getElementsByClassName('btn-reddit') as HTMLCollectionOf<HTMLLinkElement>
        this.youtubeLinks = document.getElementsByClassName('btn-youtube') as HTMLCollectionOf<HTMLLinkElement>

        for(const button of this.careersButtons) {
            this.careersButtonInnerHtml = this.careersButtonInnerHtml ?? button.innerHTML;
            (button as HTMLLinkElement).onclick = () => {
                StorageWrapper.setCareersNotificationBadgeVisible(false)
                this.setCareersButtonBadgeVisible(false)
            }
        }

        if(StorageWrapper.getCareersNotificationBadgeVisible()) this.setCareersButtonBadgeVisible(true)

        this.updatePatchNoteVersion()
        this.registerPatchNotePopupListener()
        this.setLanguageButton()
        this.setSocialMediaClickActions()
    }

    private setCareersButtonBadgeVisible(state: boolean) {
        for(const button of this.careersButtons) {
            setElementInnerHTML(button, {key: 'strings:colonistCommon.utils.#buffer', options: {value: this.careersButtonInnerHtml}})
            if(state) createSpanIn(button, 'link-badge', {key: 'strings:colonistCommon.utils.emptyString'})
        }
    }

    hide() {
        this.desktopFooter.style.display = 'none'
    }

    show() {
        if(PIXI.utils.isMobile.phone && !UIUtilsEjsVariables.isUserOnDiscordMobile) return
        this.desktopFooter.style.display = 'block'
    }

    private updatePatchNoteVersion() {
        if(this.patchnotesButtons === null) return
        const version = StorageWrapper.getVersionNo()
        if(version == undefined) return

        for(const button of this.patchnotesButtons) {
            setElementInnerHTML(button, {key: 'strings:colonistCommon.utils.#buffer', options: {value: 'v' + version.toString()}})
        }
    }

    private registerPatchNotePopupListener() {
        if(this.patchnotesButtons === null) return

        for(const button of this.patchnotesButtons) {
            const patchnotesButtonParent = button.parentElement as HTMLElement
            patchnotesButtonParent.style.display = this.inlineBlockText
            patchnotesButtonParent.style.width = this.autoText
            button.onclick = () => {
                Sound.playSound(Sound.SoundType.Click)
                CA.sendGoogleAnalyticsEvent(CA.Category.Lobby, CA.Label.ClickedLobbyPatchnotes)
            }
        }
    }

    private setLanguageButton() {
        const languageButtons = document.getElementsByName('footer_language')
        languageButtons.forEach((languageButton: HTMLElement) => {
            const languageDisplayName = clientIntl.getLanguageName(UIUtilsEjsVariables.language) as string
            languageButton.replaceChildren()
            languageButton.onclick = () => {
                new ViewPopupLanguage()
            }
            const isUserOnMobile = UIUtilsEjsVariables.isUserOnMobileApp || PIXI.utils.isMobile.phone
            if(isUserOnMobile) {
                languageButton.style.display = this.inlineBlockText
                languageButton.style.width = this.autoText
            }
            const languageNameDiv = createDivIn(languageButton, '')
            setElementInnerHTML(languageNameDiv, {key: 'strings:colonistCommon.utils.#buffer', options: {value: languageDisplayName}})
            createTooltipIn(languageButton, {key: 'strings:homePage.footer.changeLanguageButtonTooltip'}, 'top', undefined, 'small')
        })
    }

    private setSocialMediaClickActions() {
        for(const link of this.discordLinks) {
            link.onclick = () => UIWFooterController.sendSocialMediaClickAnalytic(SocialMediaLinkType.Discord)
        }
        for(const link of this.facebookLinks) {
            link.onclick = () => UIWFooterController.sendSocialMediaClickAnalytic(SocialMediaLinkType.Facebook)
        }
        for(const link of this.twitterLinks) {
            link.onclick = () => UIWFooterController.sendSocialMediaClickAnalytic(SocialMediaLinkType.Twitter)
        }
        for(const link of this.instagramLinks) {
            link.onclick = () => UIWFooterController.sendSocialMediaClickAnalytic(SocialMediaLinkType.Instagram)
        }
        for(const link of this.redditLinks) {
            link.onclick = () => UIWFooterController.sendSocialMediaClickAnalytic(SocialMediaLinkType.Reddit)
        }
        for(const link of this.youtubeLinks) {
            link.onclick = () => UIWFooterController.sendSocialMediaClickAnalytic(SocialMediaLinkType.Youtube)
        }
    }

    private static sendSocialMediaClickAnalytic(linkType: SocialMediaLinkType) {
        const eventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.SocialMediaFooterLink,
            category: AnalyticEventCategory.Footer,
            floatValue: linkType,
        }
        void CA.sendAnalyticEvent(eventData)
    }
}
